/**
 * Runtime configuration contract for the public platform UI.
 *
 * Values are read synchronously from the `#app-config` data block that ships inside
 * `index.html` — filled at build time from `.env.*` for local and legacy builds, and
 * rewritten at deploy time from the platform manifest for harbor targets. Reading from the
 * document means configuration exists before any module executes, so the accessor is
 * non-optional and there is no bootstrap ordering contract for consumers to honor.
 *
 * Only public values ever appear here — never secrets. `STRIPE_API_KEY` is a publishable
 * key.
 */
export interface RuntimeConfig {
    PLATFORM_ROOT_DOMAIN: string
    PUB_PLAT_URL: string
    INT_PLAT_URL: string
    PUB_API_URL: string
    INT_API_URL: string
    BATCH_API_URL: string
    INSIGHTS_URL: string
    CDN_URL: string
    MEDIA_URL: string
    PLATFORM_DOMAIN_KEY: string
    STRIPE_API_KEY?: string
    WHITELABEL_OVERRIDE?: string
    MAINT_MODE?: string
    MAINT_MESSAGE?: string
}

/**
 * The keys the app cannot boot without, in declared order, mirroring the leading entries of
 * the app's `config` mapping in the platform's frontend-apps declaration and the build-time
 * serializer in `config/app-config.js`.
 *
 * Validating against this list is load-bearing rather than defensive: a block that was
 * never handed its configuration must fail at boot instead of leaving the app to run
 * against undefined endpoints.
 *
 * `STRIPE_API_KEY` and `WHITELABEL_OVERRIDE` are declared but not required: the whitelabel
 * override is absent unless a deploy resolves one, and payments degrade rather than block a
 * boot. `MAINT_MODE` and `MAINT_MESSAGE` are likewise optional — an operator sets them on a
 * live target, and an absent or blank value reads as off, so requiring them would make an
 * incident lever able to prevent a boot.
 */
export const REQUIRED_CONFIG_KEYS: Array<keyof RuntimeConfig> = [
    'PLATFORM_ROOT_DOMAIN',
    'PUB_PLAT_URL',
    'INT_PLAT_URL',
    'PUB_API_URL',
    'INT_API_URL',
    'BATCH_API_URL',
    'INSIGHTS_URL',
    'CDN_URL',
    'MEDIA_URL',
    'PLATFORM_DOMAIN_KEY',
]

const APP_CONFIG_ELEMENT_ID = 'app-config'

/**
 * Reads and validates the configuration block from the current document.
 *
 * A key present with an empty string is accepted: an environment may legitimately resolve a
 * declared value to blank. A key that is absent entirely is not, because that is what an
 * unfilled block looks like.
 */
export function readAppConfig(): RuntimeConfig {
    const el = document.getElementById(APP_CONFIG_ELEMENT_ID)
    if (!el) {
        throw new Error(`Missing #${APP_CONFIG_ELEMENT_ID} block in index.html`)
    }

    const contents = el.textContent?.trim()
    if (!contents) {
        throw new Error(`Empty #${APP_CONFIG_ELEMENT_ID} block in index.html`)
    }

    let parsed: unknown
    try {
        parsed = JSON.parse(contents)
    } catch (err) {
        throw new Error(`Unparseable #${APP_CONFIG_ELEMENT_ID} block in index.html: ${(err as Error).message}`)
    }

    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        throw new Error(`Invalid #${APP_CONFIG_ELEMENT_ID} block in index.html: expected a JSON object`)
    }

    const cfg = parsed as Record<string, unknown>
    const missing = REQUIRED_CONFIG_KEYS.filter((key) => typeof cfg[key] !== 'string')
    if (missing.length > 0) {
        throw new Error(`Incomplete #${APP_CONFIG_ELEMENT_ID} block in index.html: missing ${missing.join(', ')}`)
    }

    return cfg as unknown as RuntimeConfig
}

const runtimeConfig: RuntimeConfig = readAppConfig()

/**
 * Returns the runtime configuration read from the document at module load.
 */
export function getRuntimeConfig(): RuntimeConfig {
    return runtimeConfig
}
